export interface CostItem {
  item: string;
  low: string;
  mid: string;
  high: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface CostGuide {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  updatedDate: string;
  tradeSlug: string;
  tradeName: string;
  costs: CostItem[];
  vatNote?: string;
  factors: string[];
  tips: string[];
  faq: FaqItem[];
}

export const COST_GUIDES: CostGuide[] = [
  // ─── 4 primary guides (fully enriched) ───────────────────────────

  {
    slug: "new-boiler-cost",
    title: "How Much Does a New Boiler Cost?",
    metaTitle: "How Much Does a New Boiler Cost in 2026?",
    metaDescription:
      "UK boiler replacement costs from £1,500 to £4,500+. Realistic prices for combi, system, and heat-only boilers. Find Gas Safe engineers near you for free.",
    intro:
      "Replacing your boiler is one of those jobs you can't put off once it starts playing up. Costs vary a lot depending on what you've got now, what you're switching to, and how much pipework needs changing. A straightforward combi-to-combi swap is the simplest job. Converting from a system boiler or moving the boiler to a different room adds time and materials.",
    updatedDate: "April 2026",
    tradeSlug: "gas-engineer",
    tradeName: "Gas Engineer",
    vatNote:
      "All prices include VAT at 20%. Domestic boiler installation is standard-rated.",
    costs: [
      {
        item: "Combi-to-combi swap",
        low: "£1,500",
        mid: "£2,000",
        high: "£2,500",
      },
      {
        item: "System boiler replacement",
        low: "£2,000",
        mid: "£2,800",
        high: "£3,500",
      },
      {
        item: "Conversion from system to combi",
        low: "£2,500",
        mid: "£3,200",
        high: "£4,000",
      },
      {
        item: "Back boiler removal + new combi",
        low: "£3,000",
        mid: "£3,800",
        high: "£4,500",
      },
      {
        item: "Boiler relocation (different room)",
        low: "£3,000",
        mid: "£3,500",
        high: "£5,000",
      },
    ],
    factors: [
      "Boiler type is the biggest factor. A like-for-like combi swap needs minimal pipework changes. Converting from a system or regular boiler to a combi means removing the hot water cylinder and rerouting pipes, which adds a full day or more of labour.",
      "Where the boiler lives matters. If it's staying in the same spot, the installer can usually reuse existing gas and water connections. Moving it means running new pipes, possibly through walls and floors.",
      "London and the South East are typically 15 to 25% more expensive than the Midlands or North. A job that costs £2,000 in Manchester might be £2,400 in London.",
      "The boiler brand affects the upfront cost and the warranty length. Worcester Bosch and Vaillant tend to sit at the higher end. Ideal, Baxi, and Glow-worm offer solid mid-range options. A boiler with a 10-year warranty at £200 more often makes better sense than chasing the cheapest unit.",
      "Older properties sometimes need a system flush (powerflush) to clean out sludge before fitting a new boiler. This typically costs £300 to £500 on top.",
    ],
    tips: [
      "Get at least three written quotes and make sure they specify the boiler model, not just \"a new combi boiler.\" You want to compare like for like.",
      "Summer and early autumn are quieter for heating engineers. You'll often get better availability and sometimes lower prices than the winter rush.",
      "Ask whether the quote includes the Building Regulations notification. This is a legal requirement for gas work. Most Gas Safe engineers include it, but check.",
      "If your boiler is over 15 years old but still working, get a service first. Sometimes a repair is all you need. But if it's breaking down regularly, replacement usually works out cheaper over a couple of years.",
      "Check for manufacturer cashback offers. Boiler brands regularly run promotions, especially in the quieter months.",
    ],
    faq: [
      {
        question: "How do I know if I need a new boiler?",
        answer:
          "Common signs include frequent breakdowns, rising energy bills, uneven heating, and the boiler being over 15 years old. If your engineer says parts are no longer available, it's time. A boiler that costs £300+ a year in repairs is usually cheaper to replace.",
      },
      {
        question: "How long does a boiler installation take?",
        answer:
          "A simple combi-to-combi swap usually takes one day. Converting from a system boiler or relocating the boiler can take two to three days. Your installer should give you a timeline in the quote.",
      },
      {
        question: "Should I get a combi, system, or heat-only boiler?",
        answer:
          "For most homes with one bathroom, a combi is the best choice because it heats water on demand and doesn't need a cylinder. Larger homes with two or more bathrooms often benefit from a system boiler with a cylinder, as it can supply multiple outlets at once.",
      },
      {
        question: "Do I need to upgrade my radiators when I get a new boiler?",
        answer:
          "Not usually. If your radiators are in good condition and heat up evenly, they'll work fine with a new boiler. If some rooms are cold or radiators have pinhole leaks, it's worth replacing those while the system is being drained.",
      },
      {
        question: "Is it worth paying more for a longer warranty?",
        answer:
          "Generally yes. The difference between a 5-year and 10-year warranty is often £200 to £400 upfront, but a single boiler repair can cost £150 to £300. A longer warranty protects against the most expensive early failures.",
      },
    ],
  },

  {
    slug: "house-rewiring-cost",
    title: "How Much Does Rewiring a House Cost?",
    metaTitle: "How Much Does Rewiring a House Cost in 2026?",
    metaDescription:
      "UK house rewiring costs from £2,500 to £10,000+. Realistic prices by property size and type. Find qualified electricians near you for free.",
    intro:
      "A full rewire is one of the most disruptive but important jobs you can do to an older property. The age of a property alone does not establish whether it needs a full rewire. Ask a registered electrician to assess the condition of the installation and explain whether repairs, a partial rewire or a full rewire are appropriate. Ongoing inspection and maintenance still matter after work is completed.",
    updatedDate: "April 2026",
    tradeSlug: "electrician",
    tradeName: "Electrician",
    vatNote:
      "Prices include VAT. Domestic electrical work is standard-rated at 20%.",
    costs: [
      {
        item: "1-bed flat",
        low: "£2,500",
        mid: "£3,500",
        high: "£4,500",
      },
      {
        item: "2-bed terraced house",
        low: "£3,000",
        mid: "£4,500",
        high: "£5,500",
      },
      {
        item: "3-bed semi-detached",
        low: "£4,500",
        mid: "£6,000",
        high: "£7,500",
      },
      {
        item: "4-bed detached",
        low: "£6,500",
        mid: "£8,000",
        high: "£10,000",
      },
      {
        item: "Partial rewire (per circuit)",
        low: "£400",
        mid: "£600",
        high: "£800",
      },
      {
        item: "Consumer unit replacement only",
        low: "£350",
        mid: "£500",
        high: "£650",
      },
    ],
    factors: [
      "Wall construction is the biggest cost variable. Stud walls (timber frames with plasterboard) are easy to run cables through. Solid brick or stone walls need channels chased with a grinder, which takes much longer and creates more mess.",
      "Floor type matters too. Timber floorboards can be lifted to run cables underneath. Concrete floors mean cables need to go through the walls or ceiling void instead, adding complexity.",
      "The number of sockets and light points you want drives the total. Modern households typically need more sockets than older homes were wired for. Doubling the sockets in a room doesn't double the cost, but it adds noticeably.",
      "Access to roof spaces and under-floor voids makes a big difference. Easy access means the electrician can pull cables through quickly. Limited access means more surface-mounted trunking or more invasive work.",
      "Making good after a rewire is often quoted separately. The electrician will leave chased channels and lifted floorboards. You'll need a plasterer to patch up and potentially a decorator to finish.",
    ],
    tips: [
      "Combine a rewire with other renovation work if possible. If you're already stripping a room for plastering or a kitchen refit, the electrician gets much easier access and the disruption overlaps rather than stacking.",
      "Plan your socket and switch positions carefully before work starts. Moving a socket after the walls have been chased and plastered costs far more than getting it right first time.",
      "Ask your electrician for an itemised quote that separates the wiring work from the consumer unit and from any making good they include. This helps you compare quotes fairly.",
      "Consider adding extras while the walls are open: ethernet cabling, outdoor sockets, EV charger wiring, or smart home preparation. The marginal cost during a rewire is much lower than doing it separately later.",
      "Check whether the quote includes the Electrical Installation Certificate and Building Regulations notification. Both are legal requirements for a rewire.",
    ],
    faq: [
      {
        question: "How do I know if my house needs rewiring?",
        answer:
          "Warning signs include old round-pin sockets, fabric-covered cables, a fuse box with rewirable fuses instead of MCBs, flickering lights, and sockets that feel warm. These signs need assessment by a registered electrician; they do not establish the scope of work by themselves. Ask whether an Electrical Installation Condition Report (EICR) is appropriate and discuss the findings before agreeing remedial work.",
      },
      {
        question: "How long does a full rewire take?",
        answer:
          "A 2-bed house typically takes 5 to 7 working days for the first fix (running cables) and 1 to 2 days for the second fix (fitting sockets and switches) after plastering. A 4-bed house usually takes 7 to 10 days first fix. You'll need to allow time for plastering between the two stages.",
      },
      {
        question: "Can I live in the house during a rewire?",
        answer:
          "Yes, but it's not comfortable. You'll lose power to rooms being worked on, there will be dust everywhere, and floorboards will be lifted. Most people stay if they can, but if you have young children or work from home, it's worth considering alternatives for the first-fix phase.",
      },
      {
        question: "Do I need to redecorate after a rewire?",
        answer:
          "The rooms where cables run through the walls will need plastering where the chases were cut. This means redecorating those areas. If the electrician can route cables under floors and through loft spaces, wall damage is minimised. Discuss routing options before work starts.",
      },
      {
        question: "What's a partial rewire and is it worth it?",
        answer:
          "A partial rewire replaces the wiring in specific circuits or rooms rather than the whole house. It can make sense if most of the wiring is in good condition but one area needs replacing, for example after a loft conversion. However, if the majority of wiring is old, a full rewire is usually better value and safer.",
      },
    ],
  },

  {
    slug: "new-kitchen-cost",
    title: "How Much Does a New Kitchen Cost?",
    metaTitle: "How Much Does a New Kitchen Cost in 2026?",
    metaDescription:
      "UK kitchen costs from £5,000 to £25,000+ including units and fitting. Realistic prices for budget, mid-range, and premium kitchens. Find kitchen fitters near you for free.",
    intro:
      "A new kitchen is one of the most popular home improvements and the costs can range enormously depending on where you buy the units, what worktops you choose, and how much the layout changes. The biggest surprise for most people is that the fitting labour is often less than the cost of the kitchen itself. Here's what to budget for.",
    updatedDate: "April 2026",
    tradeSlug: "kitchen-fitter",
    tradeName: "Kitchen Fitter",
    vatNote:
      "Prices include VAT at 20%. Supply-and-fit prices include standard kitchen units from the relevant tier.",
    costs: [
      {
        item: "Budget kitchen (supply + fit, small)",
        low: "£5,000",
        mid: "£7,000",
        high: "£9,000",
      },
      {
        item: "Mid-range kitchen (supply + fit, medium)",
        low: "£8,000",
        mid: "£12,000",
        high: "£16,000",
      },
      {
        item: "Premium kitchen (supply + fit, large)",
        low: "£15,000",
        mid: "£20,000",
        high: "£25,000+",
      },
      {
        item: "Fitting labour only (average kitchen)",
        low: "£2,000",
        mid: "£3,500",
        high: "£5,500",
      },
      {
        item: "Worktop supply + fit (laminate, 3m run)",
        low: "£200",
        mid: "£400",
        high: "£600",
      },
      {
        item: "Worktop supply + fit (quartz, 3m run)",
        low: "£1,200",
        mid: "£1,800",
        high: "£2,500",
      },
      {
        item: "Electrical work (new circuits/sockets)",
        low: "£300",
        mid: "£600",
        high: "£1,000",
      },
      {
        item: "Plumbing (sink/dishwasher/washing machine)",
        low: "£200",
        mid: "£400",
        high: "£700",
      },
    ],
    factors: [
      "The kitchen units themselves are the biggest cost. Flat-pack from IKEA or Howdens trade range starts around £2,000 for a small kitchen. Rigid (pre-assembled) cabinets from mid-range suppliers cost £4,000 to £8,000. Bespoke or premium brands go much higher.",
      "Worktops make a huge difference to the bill. Laminate costs a fraction of quartz or granite, and modern laminates look far better than they used to. Solid wood sits in the middle and suits certain styles well.",
      "Changing the kitchen layout adds cost because it usually means moving plumbing and possibly electrics. Keeping the sink, cooker, and fridge in the same positions saves significant money.",
      "Most kitchens need some electrical work: new sockets, lighting changes, or a cooker circuit. This is typically quoted separately by a qualified electrician.",
      "Appliance costs are separate from fitting. A full set of new appliances (oven, hob, extractor, fridge-freezer, dishwasher) typically adds £1,500 to £4,000 on top.",
    ],
    tips: [
      "Buy units and fitting separately. Many people get a better deal buying flat-pack units (IKEA, Howdens, Wren) and hiring an independent fitter rather than using the supplier's installation service. Independent fitters often charge less and do better work.",
      "Keep the existing layout where possible. Every pipe or socket that moves adds cost. If you can work with where the plumbing and electrics already are, you'll save hundreds.",
      "Consider mixing splurges and savings. You could have IKEA carcasses with nicer doors, or laminate worktops with a premium tap. Nobody inspects the inside of your cabinets.",
      "Get the electrical and plumbing work done first, before the kitchen goes in. It's cheaper to add sockets to bare walls than to work around new cabinets.",
      "If you're on a tight budget, replacing just the doors and worktops on existing carcasses can transform a kitchen for a fraction of the cost of a full refit.",
    ],
    faq: [
      {
        question: "How long does fitting a new kitchen take?",
        answer:
          "A straightforward kitchen fitting with no layout changes typically takes 5 to 10 working days. If you're moving plumbing, adding electrics, or plastering and tiling, allow 2 to 4 weeks. Your fitter should give you a schedule before work starts.",
      },
      {
        question: "Should I buy my own kitchen or use the fitter's supplier?",
        answer:
          "Either can work well. Buying your own gives you more control over cost and choice. Using the fitter's supplier means they take responsibility for ordering and dealing with any issues. If you buy separately, make sure your fitter checks the plan before you order.",
      },
      {
        question: "Do I need building regulations approval for a new kitchen?",
        answer:
          "Not for a straightforward kitchen replacement. However, if you're adding new electrical circuits, moving gas appliances, or making structural changes like removing a wall, those specific elements may need sign-off. Your electrician and gas engineer handle the certification for their work.",
      },
      {
        question: "What's the difference between flat-pack and rigid kitchens?",
        answer:
          "Flat-pack units arrive in pieces and are assembled on site. They're cheaper and the quality has improved dramatically. Rigid units arrive pre-assembled, feel more solid, and take less time to install. For most homes, flat-pack from a reputable brand is perfectly good. The doors and worktops are what you actually see and touch daily.",
      },
      {
        question: "Is IKEA a good option for kitchens?",
        answer:
          "IKEA kitchens (METOD range) are genuinely good quality for the price. The carcasses are sturdy, the soft-close drawers work well, and the door style range has grown. The main drawback is that planning and ordering can be fiddly, and you'll want an experienced fitter who knows the IKEA system.",
      },
    ],
  },

  {
    slug: "bathroom-renovation-cost",
    title: "How Much Does a Bathroom Renovation Cost?",
    metaTitle: "How Much Does a Bathroom Renovation Cost in 2026?",
    metaDescription:
      "UK bathroom renovation costs from £3,000 to £12,000+. Realistic prices for budget refreshes to full refits. Find bathroom fitters near you for free.",
    intro:
      "A bathroom renovation ranges from swapping out a tired suite and re-tiling to ripping everything out and starting from scratch with a new layout. The cost depends mostly on whether you're keeping things in the same place and how much you spend on fixtures and tiles. Moving the toilet or shower waste is where costs jump noticeably.",
    updatedDate: "April 2026",
    tradeSlug: "bathroom-fitter",
    tradeName: "Bathroom Fitter",
    vatNote:
      "Prices include VAT at 20%. Material costs for fixtures and tiles are included in the supply-and-fit ranges.",
    costs: [
      {
        item: "Cosmetic refresh (new suite, same layout)",
        low: "£3,000",
        mid: "£4,000",
        high: "£5,500",
      },
      {
        item: "Full refit (same layout, new everything)",
        low: "£5,000",
        mid: "£7,000",
        high: "£9,000",
      },
      {
        item: "Full refit (new layout)",
        low: "£7,000",
        mid: "£9,500",
        high: "£12,000",
      },
      {
        item: "En-suite shower room",
        low: "£3,500",
        mid: "£5,000",
        high: "£7,000",
      },
      {
        item: "Wet room conversion",
        low: "£5,000",
        mid: "£7,500",
        high: "£10,000",
      },
      {
        item: "Tiling only (floor + walls, standard bathroom)",
        low: "£800",
        mid: "£1,200",
        high: "£2,000",
      },
    ],
    factors: [
      "Layout changes are the biggest cost driver. Keeping the toilet, basin, and bath or shower in their existing positions means the waste pipes and water feeds stay where they are. Moving the toilet even a short distance can add £500 to £1,000 because of soil pipe requirements.",
      "Tiling is often the single biggest labour cost in a bathroom refit. Large-format tiles and intricate patterns take longer to cut and fit. Floor-to-ceiling tiling costs more than half-height tiling with painted walls above.",
      "The fixtures you choose make a huge difference. A complete bathroom suite (bath, toilet, basin) starts around £300 for basic and goes to £2,000+ for designer brands. The visual difference between a £400 and £800 suite is often surprisingly small.",
      "Underfloor heating adds comfort but also cost. Electric underfloor heating for a bathroom typically costs £300 to £600 installed. It's much easier to fit during a renovation than afterwards.",
      "Wet rooms need tanking (waterproofing) the entire floor and sometimes walls, which adds a day or more of specialist work. The gradient on the floor must be exactly right for drainage.",
    ],
    tips: [
      "Keep the toilet where it is. This single decision saves more money than almost anything else in a bathroom renovation. Soil pipe work is expensive and often involves lifting floors.",
      "Buy your own fixtures and tiles rather than using whatever your fitter offers. You'll have more choice and can shop around for better prices. Just make sure your fitter approves the specifications before you order.",
      "Choose standard sizes for the bath and shower tray. Non-standard sizes mean custom screens, doors, and panels, all of which cost more.",
      "Consider a shower over the bath rather than a separate shower enclosure if space is tight. It's cheaper and frees up floor space.",
      "Get the room fully stripped, any plumbing rough-in done, and walls made good before tiling starts. A tiler working on properly prepared walls works faster and gives a better finish.",
    ],
    faq: [
      {
        question: "How long does a bathroom renovation take?",
        answer:
          "A cosmetic refresh takes about a week. A full refit with the same layout typically takes 10 to 14 working days. If you're changing the layout, allow 2 to 3 weeks. Delivery delays on fixtures and tiles are common, so order everything well before the start date.",
      },
      {
        question: "Do I need planning permission for a bathroom renovation?",
        answer:
          "Not for a standard bathroom refit. You only need planning permission if you're extending the house to create a new bathroom, or in rare cases if your property is listed. Building regulations may apply to the electrical work and drainage changes, but your plumber and electrician handle those certifications.",
      },
      {
        question: "Can I tile over existing tiles?",
        answer:
          "Technically yes, if the existing tiles are firmly bonded and the wall can take the extra weight. In practice, most fitters recommend removing old tiles because it gives a much better result and avoids problems down the line. The extra cost of removal is usually worth it.",
      },
      {
        question: "Is underfloor heating worth it in a bathroom?",
        answer:
          "Most people who fit it say yes. A bathroom is the one room where you regularly stand on a cold floor with bare feet. Electric underfloor heating is relatively affordable (£300 to £600 installed) and costs pennies a day to run. It's much easier to fit during a renovation than retrofitting later.",
      },
      {
        question: "Should I get a sole trader or a larger company?",
        answer:
          "Both approaches work. A sole trader or small team who handles plumbing, tiling, and fitting can be cheaper and more flexible. A larger company may provide guarantees and project management. The key is checking references and seeing examples of previous bathroom work, regardless of the size of the operation.",
      },
    ],
  },

  // ─── Existing guides (updated to new format) ────────────────────

  {
    slug: "plumber-cost",
    title: "How Much Does a Plumber Cost Per Hour?",
    metaTitle: "How Much Does a Plumber Cost Per Hour in 2026?",
    metaDescription:
      "Average UK plumber hourly rates from £40 to £80. Compare prices for common plumbing jobs and find plumbers near you for free.",
    intro:
      "Plumbers typically charge by the hour, with rates varying based on location, experience, and the type of work. Many also charge a call-out fee on top of their hourly rate.",
    updatedDate: "April 2026",
    tradeSlug: "plumber",
    tradeName: "Plumber",
    costs: [
      { item: "Hourly rate", low: "£40", mid: "£60", high: "£80" },
      { item: "Call-out fee", low: "£50", mid: "£75", high: "£100" },
      { item: "Full day rate", low: "£250", mid: "£350", high: "£450" },
    ],
    factors: [
      "Emergency call-outs, especially evenings and weekends, can cost double the standard rate. Always ask about out-of-hours pricing upfront.",
      "The complexity of the job affects cost. A simple tap replacement is very different from re-plumbing a bathroom.",
      "Rates in London and major cities are consistently higher. Expect to pay 20 to 40% more in central London compared to regional averages.",
      "Experienced plumbers with Gas Safe registration or other specialist qualifications may charge more, but bring additional skills and safety assurance.",
    ],
    tips: [
      "For non-urgent work, book during weekday daytime hours to avoid emergency and weekend surcharges.",
      "Ask for a fixed price quote rather than an hourly rate when possible. This protects you if the job takes longer than expected.",
      "Combine small jobs into one visit. Getting a dripping tap, a running toilet, and a radiator bleed done in one call-out is far cheaper than three separate visits.",
      "Check whether the quote includes materials. Some plumbers mark up parts significantly, while others charge materials at cost.",
    ],
    faq: [
      {
        question: "Should I use a plumber or a heating engineer?",
        answer:
          "For general plumbing (taps, toilets, pipes, bathrooms), a standard plumber is fine. For anything involving gas (boilers, gas hobs, gas fires), you need a Gas Safe registered engineer. Some plumbers hold both qualifications.",
      },
      {
        question: "How much does it cost to fix a leaking pipe?",
        answer:
          "A straightforward pipe repair typically costs £80 to £150 including the call-out fee. If the leak is behind a wall or under a floor, the access work pushes the price higher. Emergency leak repairs outside normal hours can cost £150 to £300.",
      },
      {
        question: "Do plumbers charge for quotes?",
        answer:
          "Many plumbers offer free quotes for larger jobs like bathroom fitting or re-plumbing. For smaller repair work, they may charge a call-out fee that gets absorbed into the job cost if you go ahead. Always ask before they visit.",
      },
      {
        question: "How do I find a good plumber?",
        answer:
          "Personal recommendations are the most reliable. Failing that, look for plumbers with verified reviews and check they have the right qualifications for the work. For gas work, verify their Gas Safe registration number on the official register.",
      },
    ],
  },

  {
    slug: "electrician-cost",
    title: "How Much Does an Electrician Cost?",
    metaTitle: "How Much Does an Electrician Cost in 2026?",
    metaDescription:
      "Average UK electrician costs from £45 to £85 per hour. Compare prices for common electrical work and find electricians near you for free.",
    intro:
      "Electricians handle everything from fitting a new socket to full house rewires. Costs depend on the scope of work and whether certification is needed.",
    updatedDate: "April 2026",
    tradeSlug: "electrician",
    tradeName: "Electrician",
    costs: [
      { item: "Hourly rate", low: "£45", mid: "£65", high: "£85" },
      {
        item: "New socket or switch",
        low: "£80",
        mid: "£120",
        high: "£150",
      },
      {
        item: "Consumer unit replacement",
        low: "£350",
        mid: "£475",
        high: "£600",
      },
      {
        item: "Full house rewire (3-bed)",
        low: "£4,500",
        mid: "£6,000",
        high: "£7,500",
      },
    ],
    factors: [
      "Electrical work that requires Part P certification (notifiable work) costs more because of the inspection and sign-off process.",
      "Replacing a consumer unit (fuse box) is one of the most common jobs and prices vary by the complexity of your existing wiring.",
      "Adding circuits or sockets to older properties often uncovers issues that need rectifying to meet current regulations.",
      "NICEIC or NAPIT registered electricians can self-certify their work, which saves time and money compared to using a non-registered electrician who needs separate inspection.",
    ],
    tips: [
      "For notifiable work (new circuits, consumer units, bathroom electrics), always use an electrician registered with a competent person scheme like NICEIC or NAPIT. They can self-certify, saving you the cost of a separate building control inspection.",
      "Bundle multiple small jobs together. Adding three sockets in one visit is proportionally much cheaper than three separate appointments.",
      "Ask whether the quote includes the electrical certificate. This is a legal requirement for notifiable work and some electricians list it as an extra.",
      "Get a pre-purchase electrical survey (EICR) if you're buying an older property. It costs £150 to £300 but reveals exactly what needs doing.",
    ],
    faq: [
      {
        question: "What is Part P and does it apply to my work?",
        answer:
          "Part P of the Building Regulations covers electrical safety in dwellings. Notifiable work includes new circuits, consumer unit replacements, and any electrical work in bathrooms or outdoors. Minor work like replacing a socket or light switch is not notifiable.",
      },
      {
        question: "How long does a consumer unit replacement take?",
        answer:
          "Typically 4 to 8 hours depending on the number of circuits and the condition of the existing wiring. Your power will be off for most of the day, so plan accordingly.",
      },
      {
        question: "Can I do any electrical work myself?",
        answer:
          "You can legally do minor work like replacing light switches, sockets (like for like), and light fittings. Anything that involves new circuits, work in bathrooms, or work outdoors must be done by a qualified electrician or inspected by building control.",
      },
      {
        question: "What qualifications should I look for?",
        answer:
          "Look for registration with NICEIC, NAPIT, or ELECSA. These are government-approved competent person schemes. An electrician registered with one of these can self-certify notifiable work, which is required by law.",
      },
    ],
  },

  {
    slug: "plastering-cost",
    title: "How Much Does Plastering Cost?",
    metaTitle: "How Much Does Plastering Cost in 2026?",
    metaDescription:
      "Average UK plastering costs from £150 to £750 per room. Compare prices for skimming and rendering and find plasterers near you for free.",
    intro:
      "Plastering costs depend on whether you need a skim coat over existing plaster, fresh plaster on bare walls, or external rendering. Most plasterers quote per room or per square metre.",
    updatedDate: "April 2026",
    tradeSlug: "plasterer",
    tradeName: "Plasterer",
    costs: [
      {
        item: "Skim coat (per room)",
        low: "£150",
        mid: "£250",
        high: "£350",
      },
      {
        item: "Full re-plaster (per room)",
        low: "£300",
        mid: "£500",
        high: "£750",
      },
      {
        item: "External render (per m\u00B2)",
        low: "£40",
        mid: "£60",
        high: "£80",
      },
    ],
    factors: [
      "Skimming over existing plaster is much cheaper and quicker than stripping back to brick and re-plastering from scratch.",
      "Room size and ceiling height are the main variables. A small box room costs half what a large living room would.",
      "Ceilings are more labour-intensive than walls, so expect to pay more if the ceiling needs doing as well.",
      "External rendering costs vary by material. Sand and cement is cheapest, monocouche and silicone renders cost more but last longer.",
    ],
    tips: [
      "If you have multiple rooms that need doing, get a price for the whole lot. Plasterers prefer multi-room jobs and will usually offer a better rate.",
      "Make sure the room is cleared of furniture before the plasterer arrives. They work fast and need clear access to every wall.",
      "Ask whether the quote includes removing old loose plaster or just skimming over the top. Stripping back adds to the cost but gives a better result on damaged walls.",
      "Allow 2 to 4 weeks for new plaster to dry fully before painting. Using a mist coat (watered-down emulsion) as the first coat helps the paint bond properly.",
    ],
    faq: [
      {
        question: "How long does plastering a room take?",
        answer:
          "An experienced plasterer can skim a standard room (walls and ceiling) in a day. Full re-plastering from scratch takes 2 to 3 days per room including drying time between coats.",
      },
      {
        question: "What's the difference between skimming and plastering?",
        answer:
          "Skimming applies a thin (2 to 3mm) layer of finish plaster over existing plasterboard or plaster. Full plastering applies a thicker base coat (undercoat) followed by a skim. You need full plastering on bare brick or very damaged walls.",
      },
      {
        question: "Can I plaster over old wallpaper?",
        answer:
          "It's not recommended. Wallpaper can bubble and separate under wet plaster, leaving you with a poor finish. Strip the wallpaper first, then the plasterer can assess whether the underlying surface needs a full re-plaster or just a skim.",
      },
      {
        question: "How long before I can paint new plaster?",
        answer:
          "New plaster needs 2 to 4 weeks to dry completely, depending on ventilation and room temperature. You can tell it's dry when the colour changes from dark to a uniform light pink. Always apply a mist coat first.",
      },
    ],
  },

  {
    slug: "roofing-cost",
    title: "How Much Does a New Roof Cost?",
    metaTitle: "How Much Does a New Roof Cost in 2026?",
    metaDescription:
      "Average UK roofing costs from £5,000 to £15,000. Compare prices for re-roofing, repairs, and flat roofs. Find roofers near you for free.",
    intro:
      "A new roof is a major investment, but costs vary hugely depending on the size of the property, the materials used, and whether any structural work is needed.",
    updatedDate: "April 2026",
    tradeSlug: "roofer",
    tradeName: "Roofer",
    costs: [
      {
        item: "Flat roof replacement (garage/extension)",
        low: "£1,500",
        mid: "£2,500",
        high: "£3,500",
      },
      {
        item: "Pitched roof (terraced/semi)",
        low: "£5,000",
        mid: "£7,000",
        high: "£9,000",
      },
      {
        item: "Pitched roof (large detached)",
        low: "£9,000",
        mid: "£12,000",
        high: "£15,000",
      },
    ],
    factors: [
      "Scaffolding is a significant additional cost, typically £500 to £1,500 depending on the property size and access.",
      "Tile choice matters. Concrete tiles are the most affordable, clay tiles cost more, and natural slate is the most expensive.",
      "If the existing roof timbers (rafters and joists) need repairs or replacement, this adds significantly to the job.",
      "Flat roofs have come a long way. EPDM rubber and fibreglass (GRP) are more expensive upfront than felt but last 30+ years.",
    ],
    tips: [
      "Get a roofer to inspect the timber structure before committing to a full re-roof. If the timbers are sound, you may only need to strip and re-tile, which is significantly cheaper.",
      "Ask whether scaffolding is included in the quote. Some roofers arrange their own scaffolding; others expect you to hire it separately.",
      "Consider timing the work for spring or summer when weather delays are less likely. Roofing in winter is possible but takes longer and costs more.",
      "If you only have leaks in one area, a repair may be more cost-effective than a full re-roof. A good roofer will give you an honest assessment.",
    ],
    faq: [
      {
        question: "How long does a new roof take?",
        answer:
          "A typical terraced or semi-detached re-roof takes 3 to 5 working days in good weather. A large detached house can take 1 to 2 weeks. Flat roof replacements are usually completed in 1 to 3 days.",
      },
      {
        question: "Do I need planning permission for a new roof?",
        answer:
          "Replacing a roof with like-for-like materials doesn't usually require planning permission. However, if you're changing the roof height, shape, or materials significantly (especially in a conservation area), you may need approval. Check with your local council.",
      },
      {
        question: "How do I know if my roof needs replacing?",
        answer:
          "Warning signs include missing or cracked tiles, sagging ridgelines, daylight visible through the roof boards, and water stains on upstairs ceilings. If repairs are becoming frequent, a full replacement is often more economical in the long run.",
      },
      {
        question: "What's the best material for a flat roof?",
        answer:
          "EPDM rubber and fibreglass (GRP) are the two best options for longevity. Both last 25 to 30+ years and are far more durable than traditional felt. EPDM is slightly cheaper and easier to repair. GRP gives a smoother, harder finish.",
      },
    ],
  },

  {
    slug: "painting-decorating-cost",
    title: "How Much Do Painters and Decorators Cost?",
    metaTitle: "How Much Do Painters and Decorators Cost in 2026?",
    metaDescription:
      "Average UK painting and decorating costs from £150 to £500 per room. Compare prices and find painters near you for free.",
    intro:
      "Painting and decorating costs depend on room size, the condition of the surfaces, and whether you want wallpapering or specialist finishes.",
    updatedDate: "April 2026",
    tradeSlug: "painter-decorator",
    tradeName: "Painter & Decorator",
    costs: [
      {
        item: "Single room (walls and ceiling)",
        low: "£150",
        mid: "£280",
        high: "£400",
      },
      {
        item: "Full house interior (3-bed)",
        low: "£1,500",
        mid: "£2,500",
        high: "£3,500",
      },
      {
        item: "House exterior",
        low: "£1,000",
        mid: "£2,000",
        high: "£3,000",
      },
    ],
    factors: [
      "Surface preparation is where most of the time goes. Walls in poor condition need filling, sanding, and priming before any paint goes on.",
      "Wallpaper hanging costs more per room than painting, especially with pattern-matched or large-scale designs.",
      "Paint quality affects both the cost and the finish. Trade-quality paint lasts longer and covers better than budget alternatives.",
      "High ceilings and stairwells require additional access equipment and take significantly longer, which increases labour costs.",
    ],
    tips: [
      "Supply your own paint if you have a specific colour in mind. Decorators typically mark up materials by 10 to 20%, so buying direct from a trade counter can save money.",
      "If you're having multiple rooms done, ask for a whole-house price. Decorators prefer longer jobs and will often discount the per-room rate.",
      "Invest in proper preparation. It's tempting to skip the sugar-soaping and filling, but good prep is the difference between a finish that lasts two years and one that lasts ten.",
      "Book well in advance for exterior work. Good decorators are busy in spring and summer, which is the best time for outdoor painting.",
    ],
    faq: [
      {
        question: "How long does it take to paint a room?",
        answer:
          "A decorator can typically paint a standard room (walls and ceiling, two coats) in 1 to 2 days depending on the prep work needed. If woodwork (skirting, doors, frames) is included, add another half day to a full day.",
      },
      {
        question: "Should I move my own furniture?",
        answer:
          "Most decorators expect you to move small items and clear surfaces. They'll usually move larger furniture to the centre of the room and cover it with dust sheets. Clearing the room completely before they arrive saves time and therefore money.",
      },
      {
        question: "Is it cheaper to paint or wallpaper?",
        answer:
          "Painting is almost always cheaper. Wallpaper hanging costs more in labour (cutting, pasting, pattern matching) and the wallpaper itself can be expensive. However, a feature wall with wallpaper can add character that paint alone can't achieve.",
      },
      {
        question: "What paint should I use?",
        answer:
          "For walls, a good trade emulsion like Dulux Trade or Crown Trade gives the best coverage and durability. Matt finishes are most popular for walls. For woodwork, use a satinwood or eggshell finish. Water-based paints are now almost as durable as oil-based and much easier to work with.",
      },
    ],
  },

  {
    slug: "extension-cost",
    title: "How Much Does a House Extension Cost?",
    metaTitle: "How Much Does a House Extension Cost in 2026?",
    metaDescription:
      "Average UK house extension costs from £1,200 to £2,500 per m\u00B2. Compare prices for single and double storey extensions. Find builders near you for free.",
    intro:
      "A house extension is one of the biggest home improvement projects you can undertake. Costs per square metre vary based on the type of extension, the finish level, and your location.",
    updatedDate: "April 2026",
    tradeSlug: "builder",
    tradeName: "Builder",
    costs: [
      {
        item: "Single storey rear (per m\u00B2)",
        low: "£1,200",
        mid: "£1,500",
        high: "£1,800",
      },
      {
        item: "Double storey (per m\u00B2)",
        low: "£1,000",
        mid: "£1,250",
        high: "£1,500",
      },
      {
        item: "Side return / infill (per m\u00B2)",
        low: "£1,500",
        mid: "£2,000",
        high: "£2,500",
      },
    ],
    factors: [
      "Planning permission and building regulations approval add to the timeline and cost. Many single storey rear extensions fall under permitted development, avoiding planning fees.",
      "Foundations are a major cost. Ground conditions vary and sometimes require deeper or more complex foundations than expected.",
      "The internal finish level makes a huge difference. A basic plastered shell with standard fittings is very different from underfloor heating, bifold doors, and a high-spec kitchen.",
      "Double storey extensions cost less per square metre than single storey because you share the roof and foundation costs across two floors.",
    ],
    tips: [
      "Check whether your extension falls under permitted development before paying for a full planning application. A lawful development certificate costs less and gives you the same certainty.",
      "Get a structural engineer involved early. Their fees (£500 to £1,500) are small relative to the overall project but prevent expensive surprises with foundations and steelwork.",
      "Consider a party wall agreement timeline if you share a wall with neighbours. The process can take 2 to 3 months, so start it early to avoid holding up the build.",
      "Get a detailed, itemised quote rather than a lump sum. This makes it easier to compare builders and to track costs as the project progresses.",
    ],
    faq: [
      {
        question: "How long does building an extension take?",
        answer:
          "A single storey rear extension typically takes 10 to 16 weeks from breaking ground to completion. A double storey extension takes 14 to 24 weeks. Add 2 to 4 months before that for planning, design, and building control approval.",
      },
      {
        question: "Do I need an architect?",
        answer:
          "For a simple rear extension, an architectural technician or a builder's in-house designer may be sufficient and cheaper. For anything complex, involving listed buildings, or where design quality is important to you, a qualified architect adds real value.",
      },
      {
        question: "What's the difference between permitted development and planning permission?",
        answer:
          "Permitted development is a general grant of planning permission that covers certain types of work without a formal application. Single storey rear extensions up to certain sizes often qualify. If your extension exceeds the limits or you're in a conservation area, you'll need a full planning application.",
      },
      {
        question: "Will an extension add value to my house?",
        answer:
          "A well-designed extension typically adds more value than it costs, especially if it adds a bedroom or a larger kitchen-living space. The return depends on your area and the quality of the build. In general, extending is more cost-effective than moving to a larger property.",
      },
    ],
  },
];

export function getGuideBySlug(slug: string): CostGuide | undefined {
  return COST_GUIDES.find((g) => g.slug === slug);
}
